import { z } from 'zod';
import { BiddingModeKindSchema, BuyingPowerStateSchema, DepositMultiplierSchema } from './bidding-mode';
import { PricingPlanTierSchema, resolvePlanForTier } from './pricing-rules';
/**
 * Client solvency rule (Phase 10 — SC-11, owner decision).
 *
 * A client may hold OPEN exposure — every bid of theirs that is still live
 * (`status: 'Under Review'`) or already standing on the real auction
 * (`relayStatus: 'queued' | 'relayed'`), counted at `max(bid, proxy max)` —
 * of at most `deposit × plan.biddingPowerCoef` (retail/dealer ×10, dealer_pro
 * ×15), further capped by an admin-set `exposureLimitChf`.
 *
 * VERIFIED TOP CLIENTS are exempt from the deposit rule and bound ONLY by the
 * admin cap: `exposureExempt: true` (explicit admin flag) or a `dealer_pro`
 * client with `verified: true`. With no admin cap an exempt client is
 * unlimited (`ceilingChf: null`).
 *
 * This module is the ONE place the ceiling is computed: apps/api enforces it
 * inside the bid-write transaction and reports it from GET /users/me/exposure,
 * apps/auction and apps/admin only ever display what the API returns.
 */
/** Wire code of the 403 the bid write answers when the ceiling is exceeded. */
export const EXPOSURE_LIMIT_EXCEEDED = 'EXPOSURE_LIMIT_EXCEEDED';
/** Wire `error` key of that 403 (mirrors the older `clients.bids.errors.*` codes). */
export const EXPOSURE_LIMIT_ERROR_KEY = 'clients.bids.errors.exposureLimit';
/** Non-negative finite number, else null — the shape every stored limit must have. */
function nonNegativeOrNull(value) {
    return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null;
}
/**
 * Read the solvency profile off a raw `users` document (native driver row).
 * PARSES defensively: a malformed deposit is 0, a malformed cap is "no cap".
 */
export function exposureProfileOf(user) {
    const plan = PricingPlanTierSchema.safeParse(user?.plan);
    return {
        depositChf: nonNegativeOrNull(user?.biddingPower) ?? 0,
        plan: plan.success ? plan.data : null,
        verified: user?.verified === true,
        exposureExempt: user?.exposureExempt === true,
        exposureLimitChf: nonNegativeOrNull(user?.exposureLimitChf),
    };
}
/** True when the deposit rule does not apply (only the admin cap does). */
export function isExposureExempt(profile) {
    return profile.exposureExempt || (profile.plan === 'dealer_pro' && profile.verified);
}
/** THE ceiling. Pure: `(profile, plans)` in, ceiling out. */
export function resolveExposureCeiling(profile, plans) {
    const coef = resolvePlanForTier(plans, profile.plan).biddingPowerCoef;
    const exempt = isExposureExempt(profile);
    const adminCap = profile.exposureLimitChf;
    if (exempt)
        return { ceilingChf: adminCap, coef, exempt };
    const depositCeiling = profile.depositChf * coef;
    return {
        ceilingChf: adminCap === null ? depositCeiling : Math.min(depositCeiling, adminCap),
        coef,
        exempt,
    };
}
/**
 * Would `requestedChf` of NEW exposure fit? `openExposureChf` must already
 * EXCLUDE the bid being replaced (raising one's own bid on a lot swaps the old
 * amount for the new one — it is not double-counted).
 */
export function evaluateExposure(openExposureChf, requestedChf, ceilingChf) {
    if (ceilingChf === null)
        return { allowed: true, availableChf: null };
    const availableChf = Math.max(0, ceilingChf - openExposureChf);
    return { allowed: openExposureChf + requestedChf <= ceilingChf, availableChf };
}
/**
 * The warn-only payload (EXPOSURE_ENFORCED=false) — the 200 response carries
 * it as `exposureWarning`, the relay audit row stores it, and the 403 body
 * (enforced mode) carries the same fields next to `code`/`error`.
 */
export const ExposureWarningSchema = z.object({
    openExposure: z.number(),
    /** null = unlimited (cannot actually warn, kept for shape stability). */
    ceiling: z.number().nullable(),
    depositChf: z.number(),
    coef: z.number(),
    requested: z.number(),
    exempt: z.boolean(),
});
/** GET /api/users/me/exposure — what the bid modal renders as «Доступно для ставок». */
export const ExposureSummarySchema = z.object({
    depositChf: z.number(),
    coef: z.number(),
    exempt: z.boolean(),
    exposureLimitChf: z.number().nullable(),
    /** null = unlimited. */
    ceilingChf: z.number().nullable(),
    openExposureChf: z.number(),
    /** null = unlimited. */
    availableChf: z.number().nullable(),
    /** Mirrors EXPOSURE_ENFORCED — false means the API only warns. */
    enforced: z.boolean(),
});
/**
 * GET /api/users/me/exposure — the bid form's full picture. A bid is gated by
 * TWO independent rules and the form must be able to name which one blocks:
 * the CHF ceiling above (deposit x coef) and the active-bid COUNT below
 * (`bids.service.ts` -> BidLimitService.canPlaceBid, 429 BID_LIMIT_EXCEEDED).
 * The count gate applies to NEW bids only — raising an existing bid on a lot
 * the client already holds is never refused for running out of slots.
 */
export const BidAllowanceSchema = ExposureSummarySchema.extend({
    /** Max simultaneous ACTIVE bids; defaults to 1 when the user doc has none. */
    bidLimit: z.number(),
    /**
     * Bid slots taken (name kept for wire compatibility). Legacy and deposit
     * mode: ACTIVE bids. Trusted mode: active bids PLUS won-but-unpaid deals
     * (see `TrustedBuyingPower.activeBidCount` in `bidding-mode.ts`).
     */
    activeBidCount: z.number(),
    /** max(0, bidLimit - activeBidCount). 0 = no slot left for a NEW bid. */
    remainingBids: z.number(),
    /*
     * Bidding mode (admin deals & deposits). OPTIONAL: present only for a client
     * that carries `users.bidding`; absent = the legacy rule above applies.
     * Figures come from `resolveBuyingPower` in `bidding-mode.ts`.
     */
    mode: BiddingModeKindSchema.optional(),
    /** Deposit mode only: the ×5/×10/×15 the admin chose. */
    multiplier: DepositMultiplierSchema.optional(),
    /** Deposit mode only: deposit × multiplier, CHF. */
    capChf: z.number().optional(),
    /** Deposit mode only: CHF committed in live bids. */
    committedChf: z.number().optional(),
    /** Deposit mode only: max(0, capChf − committedChf), CHF. */
    freeChf: z.number().optional(),
    /** The finance tab's «Стан». */
    state: BuyingPowerStateSchema.optional(),
    /** When the client asked for their deposit back; null/absent = never. */
    refundRequestedAt: z.coerce.date().nullish(),
});
/**
 * PATCH /api/admin/users/:id/exposure body. Every field optional, at least
 * one required; negatives and NaN are rejected by zod (`z.number()` refuses
 * NaN, `.min(0)` refuses negatives). `exposureLimitChf: null` clears the cap.
 */
export const ExposureAdminPatchSchema = z
    .object({
    exposureLimitChf: z.number().finite().min(0).nullable().optional(),
    exposureExempt: z.boolean().optional(),
    /** Written to `users.biddingPower`. */
    depositChf: z.number().finite().min(0).optional(),
})
    .strict()
    .refine((body) => Object.keys(body).length > 0, {
    message: 'At least one of exposureLimitChf, exposureExempt, depositChf is required',
});
//# sourceMappingURL=exposure.js.map